// apps/web/src/lib/base-domain-context.tsx — Phase 8 plan 08-01.
//
// Tiny React context carrying the build-time `__BASE_DOMAIN__`
// constant (substituted by Vite `define` at build time). The
// constant flows into i18n `{{baseDomain}}` interpolation so the
// signup slug helper text shows the deployment apex instead of a
// hardcoded `gastio.app` literal.
//
// Pattern mirrors TenantSlugContext in lib/tenant-resolver.tsx:
// `createContext` + `useContext` + `useMemo`. The provider lives
// at the app root in main.tsx and wraps the public signup /
// confirm / verify / onboarding routes — same subtree as
// TenantSlugProvider.
//
// Read with `useBaseDomain()` (returns the string apex). The
// default value is the build-time constant itself; consumers
// always get the live injected value because the ambient
// `declare const __BASE_DOMAIN__: string;` makes Vite substitute
// the constant at parse time, not at runtime.

import { createContext, useContext, useMemo, type ReactNode } from "react";

declare const __BASE_DOMAIN__: string;

const BaseDomainContext = createContext<string>(__BASE_DOMAIN__);

export interface BaseDomainProviderProps {
  children: ReactNode;
}

export function BaseDomainProvider(props: BaseDomainProviderProps): ReactNode {
  const value = useMemo(() => __BASE_DOMAIN__, []);
  return (
    <BaseDomainContext.Provider value={value}>
      {props.children}
    </BaseDomainContext.Provider>
  );
}

export function useBaseDomain(): string {
  return useContext(BaseDomainContext);
}
